import { useCallback, useEffect, useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { Card, Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input, Select, Textarea } from "../components/ui/Fields";
import { DataTable, type Column } from "../components/ui/DataTable";
import { ConfirmDialog, Modal } from "../components/ui/Modal";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";

/**
 * Round 45G — Moderation Console. Search a user by PUBLIC User ID, then
 * issue Ban Account / Ban Device / Ban Chat / Mute User / Ban Room, and
 * review or revoke every restriction (reason, duration, start, expiry,
 * issued by, active/revoked) — whichever table it physically lives in
 * (moderation_actions / user_bans / room_bans). Everything is enforced by
 * the Worker; this page only issues and reviews. Backend:
 * routes/admin/moderation.js (lookup / restrictions / issue / revoke).
 */
type RestrictionType = "account_ban" | "device_ban" | "ip_ban" | "chat_ban" | "mute" | "room_ban";

interface Restriction {
  key: string;
  source: "action" | "user_ban" | "ip_ban" | "room_ban";
  id: number;
  actionType: RestrictionType;
  reason: string | null;
  issuedAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  durationMinutes: number | null;
  issuedBy: string | null;
  targetPublicId: number | null;
  targetUsername: string | null;
  roomPublicId: number | null;
  roomTitle: string | null;
  deviceId: string | null;
  ip?: string | null;
  isActive: boolean;
}

interface Lookup {
  user: { id: string; publicId: number; username: string; avatarUrl: string | null };
  restrictions: Restriction[];
  isAdministrator: boolean;
  ips: { ip: string; firstSeenAt: string; lastSeenAt: string }[];
  deviceCount: number;
  lastDeviceSeenAt: string | null;
  rooms: { id: number; publicId: number; title: string; isLive: boolean }[];
}

const LABELS: Record<RestrictionType, string> = {
  account_ban: "Ban Account",
  device_ban: "Ban Device",
  ip_ban: "Ban IP",
  chat_ban: "Ban Chat",
  mute: "Mute User",
  room_ban: "Ban Room",
};

const DESCRIPTIONS: Record<RestrictionType, string> = {
  account_ban: "Logs the user out immediately, ends their session and blocks sign-in until it expires.",
  device_ban: "Bans the device(s) this user signed in from: no account can log in or be created there.",
  ip_ban: "Bans an IP address this user signed in from: nobody can log in or create an account from it until it expires. Shared networks (offices, mobile carriers) affect everyone behind the address.",
  chat_ban: "Blocks room chat and private messages.",
  mute: "The user can join rooms and sit, but cannot use the microphone.",
  room_ban: "Ends and blocks the user's room: hidden from listings, nobody can enter it.",
};

const DURATIONS: { label: string; value: string }[] = [
  { label: "1 hour", value: "60" },
  { label: "24 hours", value: "1440" },
  { label: "7 days", value: "10080" },
  { label: "30 days", value: "43200" },
  { label: "Forever", value: "forever" },
];

function durationLabel(minutes: number | null): string {
  if (minutes === null) return "Forever";
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)} h`;
  return `${Math.round(minutes / (60 * 24))} d`;
}

export function ModerationHistoryPage() {
  const [statusFilter, setStatusFilter] = useState<"active" | "all">("active");
  const [typeFilter, setTypeFilter] = useState("");
  const [searchId, setSearchId] = useState("");
  const [found, setFound] = useState<Lookup | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const [rows, setRows] = useState<Restriction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [issueType, setIssueType] = useState<RestrictionType | null>(null);
  const [issueReason, setIssueReason] = useState("");
  const [issueDuration, setIssueDuration] = useState("1440");
  const [isIssuing, setIsIssuing] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState<Restriction | null>(null);
  const [issueIp, setIssueIp] = useState("");
  const [isRevoking, setIsRevoking] = useState(false);
  const { showToast } = useToast();

  const loadGlobal = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { status: statusFilter };
      if (typeFilter) params.type = typeFilter;
      setRows(await apiRequest<Restriction[]>("/admin/moderation/restrictions", { params }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load restrictions");
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    if (!found) void loadGlobal();
  }, [found, loadGlobal]);

  async function lookup(publicId: string) {
    setIsSearching(true);
    setSearchError(null);
    try {
      setFound(await apiRequest<Lookup>("/admin/moderation/lookup", { params: { publicId } }));
    } catch (err) {
      setFound(null);
      setSearchError(err instanceof ApiError ? err.message : "Lookup failed");
    } finally {
      setIsSearching(false);
    }
  }

  async function reloadAfterChange() {
    if (found) await lookup(String(found.user.publicId));
    else await loadGlobal();
  }

  async function handleIssue() {
    if (!found || !issueType) return;
    setIsIssuing(true);
    try {
      await apiRequest("/admin/moderation/issue", {
        method: "POST",
        body: {
          targetUserId: found.user.id,
          actionType: issueType,
          reason: issueReason.trim(),
          durationMinutes: issueDuration === "forever" ? "forever" : Number(issueDuration),
          ...(issueType === "ip_ban" && issueIp ? { ip: issueIp } : {}),
        },
      });
      showToast(`${LABELS[issueType]} issued`, "success");
      setIssueType(null);
      setIssueReason("");
      await reloadAfterChange();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not issue restriction", "error");
    } finally {
      setIsIssuing(false);
    }
  }

  async function handleRevoke() {
    if (!revokeTarget) return;
    setIsRevoking(true);
    try {
      await apiRequest("/admin/moderation/restrictions/revoke", {
        method: "POST",
        body: { source: revokeTarget.source, id: revokeTarget.id },
      });
      showToast("Restriction lifted", "success");
      setRevokeTarget(null);
      await reloadAfterChange();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not revoke", "error");
    } finally {
      setIsRevoking(false);
    }
  }

  const tableRows = found ? found.restrictions : rows;

  const columns: Column<Restriction>[] = [
    {
      header: "User",
      render: (row) => (
        <span className="text-xs">
          {row.targetUsername ?? "—"}
          {row.targetPublicId != null && <span className="block font-mono text-ink-faint">ID {row.targetPublicId}</span>}
          {row.roomPublicId != null && (
            <span className="block text-ink-faint">
              Room {row.roomPublicId} · {row.roomTitle}
            </span>
          )}
          {row.deviceId && <span className="block font-mono text-ink-faint">Device {row.deviceId}</span>}
          {row.ip && <span className="block font-mono text-ink-faint">IP {row.ip}</span>}
        </span>
      ),
    },
    { header: "Type", render: (row) => <Badge tone={row.isActive ? "danger" : "neutral"}>{LABELS[row.actionType]}</Badge> },
    { header: "Reason", render: (row) => <span className="block max-w-xs truncate text-xs" title={row.reason ?? ""}>{row.reason ?? "—"}</span> },
    { header: "Duration", render: (row) => durationLabel(row.durationMinutes) },
    { header: "Started", render: (row) => new Date(row.issuedAt).toLocaleString() },
    { header: "Expires", render: (row) => (row.expiresAt ? new Date(row.expiresAt).toLocaleString() : "Never") },
    { header: "Issued by", render: (row) => <span className="text-xs text-ink-faint">{row.issuedBy ?? "—"}</span> },
    {
      header: "Status",
      render: (row) =>
        row.revokedAt ? <Badge tone="neutral">Revoked</Badge> : row.isActive ? <Badge tone="danger">Active</Badge> : <Badge tone="neutral">Expired</Badge>,
    },
    {
      header: "",
      render: (row) =>
        row.isActive ? (
          <Button variant="secondary" onClick={() => setRevokeTarget(row)}>
            {row.actionType === "room_ban" ? "Restore room" : "Revoke"}
          </Button>
        ) : (
          "—"
        ),
    },
  ];

  const actionButtons: RestrictionType[] = ["account_ban", "device_ban", "ip_ban", "chat_ban", "mute", "room_ban"];

  return (
    <AppShell title="Moderation Console">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        Search a user by public User ID, then issue a restriction. Every restriction is enforced server-side; the user
        sees the reason and time remaining in the app. Revoke lifts it immediately (a banned room is restored).
      </p>

      <Card className="max-w-4xl p-5">
        <div className="flex flex-wrap items-end gap-3">
          <Input
            label="Public User ID"
            placeholder="e.g. 74848506"
            value={searchId}
            onChange={(e) => {
              setSearchId(e.target.value);
              if (!e.target.value.trim()) {
                setFound(null);
                setSearchError(null);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && searchId.trim()) void lookup(searchId.trim());
            }}
          />
          <Button onClick={() => lookup(searchId.trim())} isLoading={isSearching} disabled={!searchId.trim()}>
            Search
          </Button>
          {found && (
            <Button
              variant="secondary"
              onClick={() => {
                setFound(null);
                setSearchId("");
              }}
            >
              Clear
            </Button>
          )}
        </div>
        {searchError && <p className="mt-1.5 text-xs text-danger">{searchError}</p>}

        {found && (
          <div className="mt-5 border-t border-line pt-4">
            <div className="flex items-center gap-3">
              <Avatar src={found.user.avatarUrl} className="h-12 w-12 rounded-full object-cover" />
              <div>
                <p className="text-sm font-semibold">{found.user.username}</p>
                <p className="font-mono text-xs text-ink-faint">ID {found.user.publicId}</p>
                <p className="text-xs text-ink-faint">
                  {found.deviceCount > 0
                    ? `${found.deviceCount} known device${found.deviceCount > 1 ? "s" : ""}`
                    : "No device recorded yet (Ban Device unavailable)"}
                  {found.rooms.length > 0 ? ` · Room ${found.rooms.map((r) => r.publicId).join(", ")}` : " · No room"}
                </p>
                <p className="text-xs text-ink-faint">
                  {found.ips.length > 0
                    ? `IPs: ${found.ips.map((i) => i.ip).join(", ")}`
                    : "No IP recorded yet (Ban IP unavailable)"}
                </p>
                {found.isAdministrator && (
                  <p className="mt-1 text-xs font-semibold text-danger">
                    This user is an administrator — they cannot be restricted. Remove their admin permissions first (User Permissions).
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {actionButtons.map((t) => (
                <Button
                  key={t}
                  variant={t === "mute" || t === "chat_ban" ? "secondary" : "primary"}
                  disabled={
                    found.isAdministrator ||
                    (t === "device_ban" && found.deviceCount === 0) ||
                    (t === "ip_ban" && found.ips.length === 0) ||
                    (t === "room_ban" && found.rooms.length === 0)
                  }
                  onClick={() => {
                    setIssueIp(found.ips[0]?.ip ?? "");
                    setIssueType(t);
                    setIssueReason("");
                    setIssueDuration("1440");
                  }}
                >
                  {LABELS[t]}
                </Button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {!found && (
        <Card className="mt-4 max-w-4xl p-5">
          <div className="flex flex-wrap items-end gap-3">
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as "active" | "all")}>
              <option value="active">Active only</option>
              <option value="all">All (incl. expired / revoked)</option>
            </Select>
            <Select label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All types</option>
              {actionButtons.map((t) => (
                <option key={t} value={t}>
                  {LABELS[t]}
                </option>
              ))}
            </Select>
          </div>
        </Card>
      )}

      <div className="mt-4 max-w-6xl">
        <DataTable
          columns={columns}
          rows={tableRows}
          keyField={(r) => r.key}
          isLoading={isLoading || isSearching}
          error={found ? null : error}
          onRetry={loadGlobal}
          emptyTitle={found ? "No restrictions for this user" : "No matching restrictions"}
        />
      </div>

      <Modal
        isOpen={!!issueType}
        onClose={() => !isIssuing && setIssueType(null)}
        title={issueType && found ? `${LABELS[issueType]} — ${found.user.username}` : ""}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIssueType(null)} disabled={isIssuing}>
              Cancel
            </Button>
            <Button onClick={handleIssue} isLoading={isIssuing} disabled={!issueReason.trim()}>
              Confirm {issueType ? LABELS[issueType] : ""}
            </Button>
          </>
        }
      >
        {issueType && <p className="mb-3 text-xs text-ink-faint">{DESCRIPTIONS[issueType]}</p>}
        {issueType === "ip_ban" && found && found.ips.length > 0 && (
          <div className="mb-3">
            <Select label="IP address" value={issueIp} onChange={(e) => setIssueIp(e.target.value)}>
              <option value="">All recorded IPs ({found.ips.length})</option>
              {found.ips.map((i) => (
                <option key={i.ip} value={i.ip}>
                  {i.ip} — last seen {new Date(i.lastSeenAt).toLocaleString()}
                </option>
              ))}
            </Select>
          </div>
        )}
        <Textarea label="Reason (shown to the user)" value={issueReason} onChange={(e) => setIssueReason(e.target.value)} rows={3} />
        <div className="mt-3">
          <Select label="Duration" value={issueDuration} onChange={(e) => setIssueDuration(e.target.value)}>
            {DURATIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </Select>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!revokeTarget}
        onClose={() => !isRevoking && setRevokeTarget(null)}
        onConfirm={handleRevoke}
        isLoading={isRevoking}
        isDangerous={false}
        title="Confirm"
        confirmLabel={revokeTarget?.actionType === "room_ban" ? "Restore room" : "Revoke"}
        description={revokeTarget ? `Lift this ${LABELS[revokeTarget.actionType]} now? This takes effect immediately.` : ""}
      />
    </AppShell>
  );
}
